import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { claimSignerOnBehalf } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { getApiErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Options {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	refetchDocument: () => void;
	/**
	 * Called after a successful claim with the claimed signer's id. Marks the
	 * async document generation the claim triggers as pending, so the signer
	 * becomes signable without a manual refresh. When provided it replaces the
	 * one-off `refetchDocument` (the SSE stream refetches on its own).
	 */
	onClaimed?: (signerId: string) => void;
}

export const useClaimSigner = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	refetchDocument,
	onClaimed,
}: Options) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const [claimingSignerId, setClaimingSignerId] = useState<string | null>(null);

	const claimMutation = useMutation({
		mutationFn: claimSignerOnBehalf,
		onSuccess: (_data, variables) => {
			setClaimingSignerId(null);
			showToastV2({
				state: 'success',
				message: tMipManage('documents_page.signer.claim.success'),
			});
			if (onClaimed) {
				onClaimed(variables.signerId);
			} else {
				refetchDocument();
			}
			// The document refetch does not refresh the activity events query, so
			// invalidate it directly to keep the Activity tab in sync after a claim.
			return queryClient.invalidateQueries({
				queryKey: getDocumentWorkflowEventsQueryKey({
					programmeId,
					documentWorkflowInstanceId,
				}),
			});
		},
		onError: (error: unknown) => {
			setClaimingSignerId(null);
			const message =
				getApiErrorMessage(error) ?? tMipManage('documents_page.signer.claim.error');
			showToastV2({ state: 'error', message });
		},
	});

	const onClaimSigner = (signerId: string) => {
		setClaimingSignerId(signerId);
		claimMutation.mutate({
			dealId,
			programmeId,
			documentWorkflowInstanceId,
			signerId,
		});
	};

	return { claimingSignerId, onClaimSigner };
};
